import { computed, onBeforeUnmount, ref } from 'vue'

type Point = { left: number; top: number }
type Size = { width: number; height: number }

/** Keeps a floating element fully inside the viewport. */
export const clampToViewport = (left: number, top: number, el: HTMLElement | null): Point => {
  const w = el?.offsetWidth ?? 260
  const h = el?.offsetHeight ?? 300
  return {
    left: Math.min(Math.max(0, left), window.innerWidth - w),
    top: Math.min(Math.max(0, top), window.innerHeight - h),
  }
}

/** Starts a window-level mouse drag; `onMove` receives the offset from the start point. */
function useWindowDrag(onMove: (dx: number, dy: number) => void) {
  let active = false
  let startX = 0
  let startY = 0
  const move = (e: MouseEvent) => {
    if (active) onMove(e.clientX - startX, e.clientY - startY)
  }
  const stop = () => {
    active = false
    window.removeEventListener('mousemove', move)
    window.removeEventListener('mouseup', stop)
  }
  const start = (e: MouseEvent) => {
    active = true
    startX = e.clientX
    startY = e.clientY
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', stop)
  }
  onBeforeUnmount(stop)
  return { start, stop }
}

/**
 * A draggable (by its header) and resizable (by a corner handle) floating panel,
 * clamped to the viewport.
 */
export function useFloatingPanel(initial: { position: Point; size: Size; minSize: Size }) {
  const panelRef = ref<HTMLElement | null>(null)
  const position = ref<Point>({ ...initial.position })
  const size = ref<Size>({ ...initial.size })

  let dragOrigin: Point = { left: 0, top: 0 }
  const drag = useWindowDrag((dx, dy) => {
    position.value = clampToViewport(dragOrigin.left + dx, dragOrigin.top + dy, panelRef.value)
  })

  let resizeOrigin: Size = { width: 0, height: 0 }
  const resize = useWindowDrag((dx, dy) => {
    size.value = {
      width: Math.max(initial.minSize.width, resizeOrigin.width + dx),
      height: Math.max(initial.minSize.height, resizeOrigin.height + dy),
    }
    // Keep the panel on screen after growing.
    position.value = clampToViewport(position.value.left, position.value.top, panelRef.value)
  })

  const startDrag = (e: MouseEvent) => {
    if (e.button !== 0) return
    dragOrigin = { ...position.value }
    drag.start(e)
  }

  const startResize = (e: MouseEvent) => {
    if (e.button !== 0) return
    e.stopPropagation()
    resizeOrigin = { ...size.value }
    resize.start(e)
  }

  const style = computed(() => ({
    left: `${position.value.left}px`,
    top: `${position.value.top}px`,
    width: `${size.value.width}px`,
    height: `${size.value.height}px`,
  }))

  return { panelRef, style, startDrag, startResize }
}

/**
 * A round floating button anchored by CSS until first dragged; a click that ends a
 * drag (moved > 3 px) is swallowed. Plain reactive left/top: transform tricks caused a
 * visible jump at drag start.
 */
export function useDraggableButton() {
  const buttonRef = ref<HTMLElement | null>(null)
  const position = ref<Point | null>(null)
  const moved = ref(false)

  let origin: Point = { left: 0, top: 0 }
  const drag = useWindowDrag((dx, dy) => {
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved.value = true
    position.value = clampToViewport(origin.left + dx, origin.top + dy, buttonRef.value)
  })

  const startDrag = (e: MouseEvent) => {
    if (e.button !== 0) return
    moved.value = false
    // First drag: start from where the CSS-anchored button really is.
    if (!position.value) {
      const rect = buttonRef.value?.getBoundingClientRect()
      position.value = rect
        ? { left: rect.left, top: rect.top }
        : { left: window.innerWidth - 80, top: window.innerHeight - 120 }
    }
    origin = { ...position.value }
    drag.start(e)
  }

  /** Returns true when this click is the end of a drag (and resets that state). */
  const consumeDragClick = () => {
    if (!moved.value) return false
    moved.value = false
    return true
  }

  const style = computed(() =>
    position.value
      ? {
          left: `${position.value.left}px`,
          top: `${position.value.top}px`,
          right: 'auto',
          bottom: 'auto',
        }
      : {},
  )

  return { buttonRef, moved, style, startDrag, consumeDragClick }
}
